/**
 * The administration forms for texts and queue alerts: the pure part.
 *
 * Levels are typed in minutes and sent in seconds, because nobody sets a waiting
 * alert in seconds and the server measures in them. Every refusal here is one the
 * server makes too (notify.Threshold.Problems), so the button can be off before
 * a round trip, and the server still has the last word.
 */
import type { StaffKey } from './staffText'
import { normalisePhone } from './ticketFields'
import type { QueueAlertSetting } from './types'

export interface ThresholdRow {
  queueId: string
  waitingAlert: number
  waitingReset: number
  longestAlertMin: number
  longestResetMin: number
  /** Comma-separated as typed. */
  textTo: string
  /** Comma-separated as typed. */
  emailTo: string
}

export const MAX_NUMBERS = 3
export const MAX_EMAILS = 5

// Not RFC 5322: net/mail.ParseAddress on the server is the real check.
const LOOKS_LIKE_AN_EMAIL = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/

export function rowsFrom(alerts: QueueAlertSetting[], queues: { id: string }[]): ThresholdRow[] {
  return queues.map((q) => {
    const a = alerts.find((x) => x.queueId === q.id)
    return {
      queueId: q.id,
      waitingAlert: a?.waitingAlert ?? 0,
      waitingReset: a?.waitingReset ?? 0,
      longestAlertMin: Math.round((a?.longestAlertSec ?? 0) / 60),
      longestResetMin: Math.round((a?.longestResetSec ?? 0) / 60),
      textTo: (a?.textTo ?? []).join(', '),
      emailTo: (a?.emailTo ?? []).join(', '),
    }
  })
}

export const listOf = (typed: string) =>
  typed
    .split(',')
    .map((n) => n.trim())
    .filter((n) => n !== '')

/** Addresses as the server keeps them: lower-cased, each once. */
export function emailsOf(typed: string): string[] {
  return [...new Set(listOf(typed).map((e) => e.toLowerCase()))]
}

export function rowProblem(row: ThresholdRow): StaffKey | '' {
  const pairs: Array<[number, number]> = [
    [row.waitingAlert, row.waitingReset],
    [row.longestAlertMin, row.longestResetMin],
  ]
  for (const [alert, reset] of pairs) {
    if (alert < 0 || reset < 0) return 'notify.alerts.negative'
    if (alert === 0 && reset !== 0) return 'notify.alerts.resetWithoutAlert'
    if (alert > 0 && reset >= alert) return 'notify.alerts.resetBelow'
  }
  const numbers = listOf(row.textTo)
  if (numbers.length > MAX_NUMBERS) return 'notify.alerts.tooManyNumbers'
  if (numbers.some((n) => normalisePhone(n) === null)) return 'notify.alerts.notANumber'
  if (listOf(row.emailTo).some((e) => !LOOKS_LIKE_AN_EMAIL.test(e))) return 'notify.alerts.notAnEmail'
  if (emailsOf(row.emailTo).length > MAX_EMAILS) return 'notify.alerts.tooManyEmails'
  return ''
}

/** What a save sends: the rows with a level, in seconds. Null when any row is wrong. */
export function alertsToSend(rows: ThresholdRow[]): QueueAlertSetting[] | null {
  if (rows.some((r) => rowProblem(r) !== '')) return null
  return rows
    .filter((r) => r.waitingAlert > 0 || r.longestAlertMin > 0)
    .map((r) => ({
      queueId: r.queueId,
      waitingAlert: r.waitingAlert,
      waitingReset: r.waitingReset,
      longestAlertSec: r.longestAlertMin * 60,
      longestResetSec: r.longestResetMin * 60,
      textTo: listOf(r.textTo).map((n) => normalisePhone(n) ?? n),
      emailTo: emailsOf(r.emailTo),
    }))
}

/** How many texts a template costs, in the reader's language: {n} SMS. */
export function segmentsLabel(n: number): { key: StaffKey; n: number } {
  return { key: 'notify.segments', n }
}
